import { catchError, firstValueFrom, from, map, mergeMap } from 'rxjs';
import { RECAPTCHA_ACTION } from '../../authentication/constant/recaptcha';
import Authorization from '../../authentication/models/authorization';
import { SaveVehicleInterface } from '../interfaces/api-vehicle-interface';
import ErrorMapping from '../mappings/error.mapping';
import Vehicle from '../models/vehicle';
import { VehicleBrand } from '../models/vehicle-brand';
import { VehicleModel } from '../models/vehicle-model';
import { VehicleSumInsured } from '../models/vehicle-sum-insured';
import VehicleProvider from '../providers/vehicle-provider';
import VehicleMapping from '../mappings/vehicle-mapping';
import BrandMapping from '../mappings/brand-mapping';
import ModelMapping from '../mappings/model-mapping';
import SumInsuredMapping from '../mappings/sum-insured-dto';
import VehicleChinese from '../models/vehicle-chinese';

class VehicleRepository {
  private auth: Authorization = Authorization.getInstance();

  constructor(
    private errorMapping: ErrorMapping,
    private vehicleMapping: VehicleMapping,
    private sumInsuredMapping: SumInsuredMapping,
  ) { }

  async queryByPlate(plate: string): Promise<Vehicle> {
    const source$ = from(this.auth.executeRecaptcha(RECAPTCHA_ACTION)).pipe(
      map((recaptchaToken) => ({
        authorization: this.auth.token,
        recaptcha: recaptchaToken,
        plate
      })),
      mergeMap(VehicleProvider.queryByPlate),
      map((result) => this.vehicleMapping.getVehicleResponse(result.response)),
      catchError(this.errorMapping.throwError),
    );

    return firstValueFrom(source$);
  }

  async getVehicleBrands(): Promise<VehicleBrand[]> {
    const source$ = VehicleProvider.getVehicleBrands().pipe(
      map((xhr) => xhr.response),
      catchError(this.errorMapping.throwError),
    );

    const result = await firstValueFrom(source$);

    return result;
  }

  async getVehicleModels(brandCode: string): Promise<VehicleModel[]> {
    const source$ = from(this.auth.executeRecaptcha(RECAPTCHA_ACTION)).pipe(
      map((recaptchaToken) => ({
        authorization: this.auth.token,
        recaptcha: recaptchaToken,
        brandCode,
      })),
      mergeMap(VehicleProvider.getVehicleModels),
      map((result) =>
        this.vehicleMapping.getVehicleModelsResponse(result.response),
      ),
      catchError(this.errorMapping.throwError),
    );

    return firstValueFrom(source$);
  }

  async getSumInsured(data: {
    brandCode: string;
    apesegBrandCode: string;
    modelCode: string;
    apesegModelCode: string;
    year: string;
  }): Promise<VehicleSumInsured> {
    const { apesegBrandCode, apesegModelCode, brandCode, modelCode, year } =
      data;
    const source$ = from(this.auth.executeRecaptcha(RECAPTCHA_ACTION)).pipe(
      map((recaptchaToken) => ({
        authorization: this.auth.token,
        recaptcha: recaptchaToken,
        brandCode,
        apesegBrandCode,
        apesegModelCode,
        modelCode,
        year,
      })),
      mergeMap(VehicleProvider.getSumInsured),
      map((result) =>
        this.sumInsuredMapping.getSumInsuredResponse(result.response),
      ),
      catchError(this.errorMapping.throwError),
    );

    return firstValueFrom(source$);
  }

  async create(vehicle: SaveVehicleInterface): Promise<any> {
    const source$ = from(this.auth.executeRecaptcha(RECAPTCHA_ACTION)).pipe(
      map((recaptchaToken) => ({
        authorization: this.auth.token,
        vehicle, recaptcha: recaptchaToken
      })),
      mergeMap(VehicleProvider.create),
      map((result) =>
        this.vehicleMapping.formatCreatePatchVehicleResponse(result.response),
      ),
      catchError(this.errorMapping.throwError),
    );

    return firstValueFrom(source$);
  }

  async update(data: {
    id: string;
    sumInsuredSelected?: number | any;
    engineSerialNumber?: string;
    chassisSerialNumber?: string;
  }): Promise<any> {
    const source$ = from(this.auth.executeRecaptcha(RECAPTCHA_ACTION)).pipe(
      map((recaptchaToken) => ({
        authorization: this.auth.token,
        recaptcha: recaptchaToken,
        ...data,
      })),
      mergeMap(VehicleProvider.update),
      map((result) =>
        this.vehicleMapping.formatCreatePatchVehicleResponse(result.response),
      ),
      catchError(this.errorMapping.throwError),
    );

    return firstValueFrom(source$);
  }

  async getChineseVehicle(id: string): Promise<VehicleChinese> {
    const source$ = from(this.auth.executeRecaptcha(RECAPTCHA_ACTION)).pipe(
      map((recaptchaToken) => ({
        authorization: this.auth.token,
        id,
        recaptcha: recaptchaToken,
      })),
      mergeMap(VehicleProvider.getChineseVehicle),
      map((result) => this.vehicleMapping.formatGetChineseVehicleResponse(result.response)),
      catchError(this.errorMapping.throwError),
    );

    return firstValueFrom(source$);
  }
}

export const vehicleRepository = new VehicleRepository(
  new ErrorMapping(),
  new VehicleMapping(
    new BrandMapping(),
    new ModelMapping(),
    new SumInsuredMapping(),
  ),
  new SumInsuredMapping(),
);
